import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../services/api';
import { 
  ChartBarIcon, 
  OfficeBuildingIcon, 
  UserGroupIcon, 
  DocumentTextIcon,
  CurrencyDollarIcon,
  ClipboardListIcon,
  ClipboardCheckIcon,
  ShieldCheckIcon
} from '@heroicons/react/outline';

export default function AdminDashboard() {
  const [aggregatedStats, setAggregatedStats] = useState(null);
  const [companyBreakdown, setCompanyBreakdown] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [statsResponse, breakdownResponse] = await Promise.all([
        api.get('/api/dashboard/stats', { headers }),
        api.get('/api/dashboard/company-breakdown', { headers })
      ]);
      
      setAggregatedStats(statsResponse.data);
      setCompanyBreakdown(breakdownResponse.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, className = "", color = "blue" }) => (
    <div className={`bg-white overflow-hidden shadow rounded-lg ${className}`}>
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <Icon className={`h-6 w-6 text-${color}-600`} aria-hidden="true" />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
              <dd className={`text-lg font-medium text-${color}-900`}>
                {typeof value === 'number' ? value.toLocaleString() : value}
              </dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );

  const CompanyCard = ({ company, onClick, isSelected }) => (
    <div 
      className={`bg-white overflow-hidden shadow rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md ${
        isSelected ? 'ring-2 ring-blue-500' : ''
      }`}
      onClick={() => onClick(company)}
    >
      <div className="p-5">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">{company.name}</h3>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {company.abbreviation}
          </span>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">{t('dashboard.branches')}</p>
            <p className="text-lg font-semibold text-gray-900">{company.branches}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">{t('dashboard.totalClients')}</p>
            <p className="text-lg font-semibold text-gray-900">{(company.clients || 0).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">{t('dashboard.totalDocuments')}</p>
            <p className="text-lg font-semibold text-gray-900">{(company.documents || 0).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">{t('dashboard.totalPolicies')}</p>
            <p className="text-lg font-semibold text-gray-900">{(company.policies || 0).toLocaleString()}</p>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex justify-between items-center mb-1">
            <span className="text-sm text-gray-500">{t('dashboard.totalPaid')}</span>
            <span className="text-sm font-medium text-green-600">
              {(company.totalPaid || 0).toLocaleString()} LYD
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">{t('dashboard.totalUnpaid')}</span>
            <span className="text-sm font-medium text-red-600">
              {(company.totalUnpaid || 0).toLocaleString()} LYD
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('navigation.dashboard')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('dashboard.adminOverview')}
          </p>
        </div>
        <button
          onClick={fetchDashboardData}
          className="btn-secondary flex items-center space-x-2"
        >
          <span>{t('dashboard.refresh')}</span>
        </button>
      </div>

      {/* Aggregated Statistics */}
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          {t('dashboard.systemOverview')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title={t('dashboard.totalCompanies')}
            value={companyBreakdown.length}
            icon={OfficeBuildingIcon}
            color="blue"
          />
          <StatCard
            title={t('dashboard.totalClients')}
            value={aggregatedStats?.totalClients || 0}
            icon={UserGroupIcon}
            color="green"
          />
          <StatCard
            title={t('dashboard.totalDocuments')}
            value={aggregatedStats?.totalDocuments || 0}
            icon={DocumentTextIcon}
            color="purple"
          />
          <StatCard
            title={t('dashboard.totalPolicies')}
            value={aggregatedStats?.totalPolicies || 0}
            icon={ClipboardListIcon}
            color="indigo"
          />
        </div>
      </div>

      {/* Secondary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Financial Overview */}
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            {t('dashboard.financialOverview')}
          </h2>
          <div className="grid grid-cols-1 gap-4">
            <StatCard
              title={t('dashboard.totalCoverageAmount')}
              value={`${(aggregatedStats?.totalLimitOfIndemnity || 0).toLocaleString()} LYD`}
              icon={CurrencyDollarIcon}
              color="green"
            />
            <StatCard
              title={t('dashboard.totalPaid')}
              value={`${(aggregatedStats?.totalPaid || 0).toLocaleString()} LYD`}
              icon={CurrencyDollarIcon}
              color="blue"
            />
            <StatCard
              title={t('dashboard.totalUnpaid')}
              value={`${(aggregatedStats?.totalUnpaid || 0).toLocaleString()} LYD`}
              icon={CurrencyDollarIcon}
              color="red"
            />
          </div>
        </div>

        {/* Claims Overview */}
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            {t('dashboard.claimsOverview')}
          </h2>
          <div className="grid grid-cols-1 gap-4">
            <StatCard
              title={t('dashboard.totalClaims')}
              value={aggregatedStats?.totalClaims || 0}
              icon={ClipboardCheckIcon}
              color="orange"
            />
            <StatCard
              title={t('dashboard.settledClaims')}
              value={aggregatedStats?.settledClaims || 0}
              icon={ClipboardCheckIcon}
              color="green"
            />
            <StatCard
              title={t('dashboard.settlementRate')}
              value={`${(aggregatedStats?.totalClaims > 0 ? Math.round(((aggregatedStats?.settledClaims || 0) / aggregatedStats.totalClaims) * 100) : 0)}%`}
              icon={ChartBarIcon}
              color="purple"
            />
          </div>
        </div>
      </div>

      {/* Company Breakdown */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">
            {t('dashboard.companyBreakdown')}
          </h2>
          {selectedCompany && (
            <button
              onClick={() => setSelectedCompany(null)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {t('dashboard.showAll')}
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {companyBreakdown.map((company) => (
            <CompanyCard
              key={company.id}
              company={company}
              onClick={setSelectedCompany}
              isSelected={selectedCompany?.id === company.id}
            />
          ))}
        </div>
      </div>

      {/* Selected Company Details */}
      {selectedCompany && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {t('dashboard.companyDetails')} - {selectedCompany.name}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                {t('dashboard.branches')} ({selectedCompany.branches})
              </h4>
              <div className="space-y-2">
                {selectedCompany.branchDetails?.map((branch) => (
                  <div key={branch.id} className="text-sm text-gray-600">
                    • {branch.name}
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                {t('dashboard.quickStats')}
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('dashboard.totalClaims')}:</span>
                  <span className="font-medium">{selectedCompany.claims || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('dashboard.totalCoverage')}:</span>
                  <span className="font-medium">{(selectedCompany.limitOfIndemnity || 0).toLocaleString()} LYD</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('dashboard.paymentRate')}:</span>
                  <span className="font-medium">
                    {(selectedCompany.totalPremium || 0) > 0 
                      ? Math.round(((selectedCompany.totalPaid || 0) / (selectedCompany.totalPremium || 1)) * 100) 
                      : 0}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('dashboard.totalPremium')}:</span>
                  <span className="font-medium">{(selectedCompany.totalPremium || 0).toLocaleString()} LYD</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 